import { TRoadblockCaseData, TRoadblockCheckListItem, TRoadblockClient, TRoadblockConfiguration } from "../roadblockTypesAndCodecs";
import { allDocumentSlotsReceived, anyDocSlotExists, probateChecklistItems } from "./ProbateItems";
import { anyReceivedDocumentExists, clientsAuthorisedExchangeAndCompleteItem, sharedExchangeAndPreCompletionItems, sourceOfWealthSatisfiedItem } from "./SharedExchangeAndPreCompletionItems";
import { TCasesDocumentType } from "../../models/CaseDocumentType1";
import { clientName, clientsOfTypes, fact, formatEvidenceDate, neutralFact } from "../evidence/evidenceHelpers";
import { CaseIlaOrDisclaimerChoice_DisplayName } from "../../codecs/CaseIlaOrDisclaimerChoice";
import { TRoadblockEvidenceFact, TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";
import { formatCurrencyFromPence } from "../../../shared/src/util/numberFormatting";

const redemptionLink: TRoadblockEvidenceLink = { block: "charges_and_restrictions", section: null };
const contractsLink: TRoadblockEvidenceLink = { block: "case_contracts", section: null };

const receivedDocDate = (c: TRoadblockCaseData, types: Array<TCasesDocumentType>): string | null =>
    c.cases_documents.reduce<string | null>(
        (latest, { type, received_date }) =>
            types.includes(type) && received_date !== null && (latest === null || received_date > latest)
                ? received_date
                : latest,
        null,
    );

const receivedDocFact = (
    c: TRoadblockCaseData,
    label: string,
    types: Array<TCasesDocumentType>,
    link: TRoadblockEvidenceLink | null = null,
): TRoadblockEvidenceFact => {
    const date = receivedDocDate(c, types);
    return fact(label, date !== null ? `Received ${formatEvidenceDate(date)}` : "Not received", date !== null, link);
};

const departingOwners = (c: TRoadblockCaseData): Array<TRoadblockClient> =>
    c.clients.filter((u) => u.ownership_status === "departing_owner");

const transferDisclaimerReceived = (c: TRoadblockCaseData) =>
    c.cases_documents.some(({ type, received_date }) =>
        type === "transfer_disclaimer" && received_date !== null
    );

const departingOwnerIlaOrDisclaimerSatisfied = (c: TRoadblockCaseData, owner: TRoadblockClient): boolean => {
    if (owner.ila_or_disclaimer_choice === "unknown") return false;
    if (owner.ila_or_disclaimer_choice === "none") return true;
    if (owner.ila_or_disclaimer_choice === "ila") return owner.ila_received_confirmed_at !== null;
    if (owner.ila_or_disclaimer_choice === "disclaimer") return transferDisclaimerReceived(c);
    return false;
};

const preCompletionReadyItemsBeforeClientsAuthorised: Array<TRoadblockCheckListItem> = [
        ...sharedExchangeAndPreCompletionItems(),

        sourceOfWealthSatisfiedItem(false),

        ...probateChecklistItems(),
        {
            type: "first_redemption_statement_received",
            is_resolved_by_short_description: "First redemption statement received for all charges",
            create: {
                create_condition_description: "Cases with at least one charge",
                predicate: (c) => c.cases_charges.length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every charge on the case having a redemption statement received against it.`,
                predicate: (c) =>
                    c.cases_charges.length > 0
                    && c.cases_charges.every((ch) => ch.has_redemption_statement),
                evidence: (c) =>
                    c.cases_charges.length === 0
                        ? [neutralFact("Charges", "None on case", redemptionLink)]
                        : c.cases_charges.map((ch) =>
                              fact(
                                  ch.account_number.length > 0 ? `Charge ${ch.account_number}` : "Charge",
                                  ch.has_redemption_statement ? "Statement received" : "No statement",
                                  ch.has_redemption_statement,
                                  redemptionLink,
                              ),
                          ),
            },
        },
        {
            type: "management_company_details_obtained",
            is_resolved_by_short_description: "Management company details obtained",
            create: {
                create_condition_description: "Cases with a management company",
                predicate: (c) => c.case.has_management_company === true,
            },
            resolve: {
                is_resolved_by_long_description: `Linking at least one management company to the case, each with a name and at least one contact channel (email or phone).`,
                predicate: (c) =>
                    c.management_companies.length > 0
                    && c.management_companies.every((m) =>
                        m.company_name.trim() !== ""
                        && (
                            (m.company_email !== null && m.company_email.trim() !== "")
                            || (m.company_phone_number !== null && m.company_phone_number.trim() !== "")
                        )
                    ),
            },
        },
        {
            type: "management_company_consent_obtained",
            is_resolved_by_short_description: "Management company consent obtained",
            create: {
                create_condition_description: "Transfer or remortgage cases with a management company",
                predicate: (c) => c.case.has_management_company === true,
            },
            resolve: {
                is_resolved_by_long_description: `Every linked management company having consent to transfer/remortgage obtained, or marked as not required.`,
                predicate: (c) =>
                    c.management_companies.length > 0
                    && c.management_companies.every((m) => m.consent_obtained !== null || m.consent_not_required),
            },
        },
        {
            type: "gift_declaration_signed",
            is_resolved_by_short_description: "Gift Declaration received",
            create: {
                create_condition_description: "Transfer or remortgage cases with at least one giftor on the case",
                predicate: (c) =>
                    (c.case.transaction_type === "transfer" || c.case.transaction_type === "remortgage")
                    && c.clients.some((client) => client.user_type === "giftor"),
            },
            resolve: {
                is_resolved_by_long_description: `Having a signed gift_declaration document uploaded against the case.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["gift_declaration"]),
                evidence: (c) => [
                    ...clientsOfTypes(c.clients, ["giftor"]).map((client) =>
                        neutralFact(clientName(client), "Giftor"),
                    ),
                    receivedDocFact(c, "Gift Declaration", ["gift_declaration"]),
                ],
            },
        },
        {
            type: "departing_owners_ila_or_disclaimer_complete",
            is_resolved_by_short_description: "ILA or disclaimer complete for every departing owner",
            create: {
                create_condition_description: "Divorce cases with at least one departing-owner client",
                predicate: (c) =>
                    c.case.seller_reason_for_sale === "divorce"
                    && departingOwners(c).length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every departing-owner client on the case having a choice recorded (none / ILA / disclaimer). 'ILA' resolves when the handler marks the client as having received independent legal advice; 'disclaimer' resolves when a signed transfer_disclaimer document is received on the case; 'none' resolves immediately.`,
                predicate: (c) => {
                    const owners = departingOwners(c);
                    return owners.length > 0 && owners.every((owner) => departingOwnerIlaOrDisclaimerSatisfied(c, owner));
                },
                evidence: (c) => departingOwners(c).map((owner) =>
                    fact(
                        clientName(owner),
                        owner.ila_or_disclaimer_choice === "unknown"
                            ? "Not recorded"
                            : CaseIlaOrDisclaimerChoice_DisplayName(owner.ila_or_disclaimer_choice),
                        departingOwnerIlaOrDisclaimerSatisfied(c, owner),
                    ),
                ),
            },
        },
        {
            type: "draft_tr1_created",
            is_resolved_by_short_description: "Draft TR1 created",
            create: {
                create_condition_description: "Transfer cases (not assents — those use AS1)",
                predicate: (c) =>
                    c.case.transaction_type === "transfer"
                    && c.case.seller_reason_for_sale !== "probate_as1",
            },
            resolve: {
                is_resolved_by_long_description: `A draft or signed TR1/TP1/TP2/TR2 being created for the case.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["draft_tr1", "signed_tr1", "draft_tp1", "signed_tp1", "draft_tp2", "signed_tp2", "draft_tr2", "signed_tr2"]),
                evidence: (c) => {
                    const created = anyReceivedDocumentExists(c, ["draft_tr1", "signed_tr1", "draft_tp1", "signed_tp1", "draft_tp2", "signed_tp2", "draft_tr2", "signed_tr2"]);
                    return [fact("Draft TR1", created ? "Created" : "Not created", created, contractsLink)];
                },
            },
        },
        {
            type: "draft_as1_created",
            is_resolved_by_short_description: "Draft AS1 created",
            create: {
                create_condition_description: "Probate assent cases (seller_reason_for_sale = probate_as1)",
                predicate: (c) => c.case.seller_reason_for_sale === "probate_as1",
            },
            resolve: {
                is_resolved_by_long_description: `A draft or signed AS1 being created for the case.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["draft_as1", "signed_as1"]),
                evidence: (c) => {
                    const created = anyReceivedDocumentExists(c, ["draft_as1", "signed_as1"]);
                    return [fact("Draft AS1", created ? "Created" : "Not created", created, contractsLink)];
                },
            },
        },
        {
            type: "signed_tr1_received",
            is_resolved_by_short_description: "Signed TR1 received",
            create: {
                create_condition_description: "Transfer cases (not assents — those use AS1) where a draft TR1 exists and a signed_tr1 slot exists",
                predicate: (c) =>
                    c.case.transaction_type === "transfer"
                    && c.case.seller_reason_for_sale !== "probate_as1"
                    && anyReceivedDocumentExists(c, ["draft_tr1", "signed_tr1", "draft_tp1", "signed_tp1", "draft_tp2", "signed_tp2", "draft_tr2", "signed_tr2"])
                    && anyDocSlotExists(c, ["signed_tr1"]),
            },
            resolve: {
                is_resolved_by_long_description: `Every signed_tr1 document slot on the case being uploaded and marked as received.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["signed_tr1"]),
                evidence: (c) => {
                    const received = allDocumentSlotsReceived(c, ["signed_tr1"]);
                    const date = receivedDocDate(c, ["signed_tr1"]);
                    return [
                        fact(
                            "Signed TR1",
                            received && date !== null ? `Received ${formatEvidenceDate(date)}` : "Not received",
                            received,
                            contractsLink,
                        ),
                    ];
                },
            },
        },
        {
            type: "signed_as1_received",
            is_resolved_by_short_description: "Signed AS1 received",
            create: {
                create_condition_description: "Probate assent cases (seller_reason_for_sale = probate_as1) where a draft AS1 exists and a signed_as1 slot exists",
                predicate: (c) =>
                    c.case.seller_reason_for_sale === "probate_as1"
                    && anyReceivedDocumentExists(c, ["draft_as1", "signed_as1"])
                    && anyDocSlotExists(c, ["signed_as1"]),
            },
            resolve: {
                is_resolved_by_long_description: `Every signed_as1 document slot on the case being uploaded and marked as received.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["signed_as1"]),
                evidence: (c) => {
                    const received = allDocumentSlotsReceived(c, ["signed_as1"]);
                    const date = receivedDocDate(c, ["signed_as1"]);
                    return [
                        fact(
                            "Signed AS1",
                            received && date !== null ? `Received ${formatEvidenceDate(date)}` : "Not received",
                            received,
                            contractsLink,
                        ),
                    ];
                },
            },
        },
        {
            type: "contract_details_added",
            is_resolved_by_short_description: "Contract details added",
            create: {
                create_condition_description: "Transfer cases",
                predicate: (c) => c.case.transaction_type === "transfer",
            },
            resolve: {
                is_resolved_by_long_description: `Setting the consideration (price) for the transfer on the case.`,
                predicate: (c) => c.case.price_pence !== null,
                evidence: (c) => [
                    fact(
                        "Consideration",
                        c.case.price_pence !== null ? formatCurrencyFromPence(c.case.price_pence) : "Not set",
                        c.case.price_pence !== null,
                        contractsLink,
                    ),
                ],
            },
        },
];

export const PreCompletionReady: TRoadblockConfiguration = {
    type: "pre_completion_ready",
    display_name: "Pre-Completion Ready",
    only_create_if_resolved: [],
    requires_authoriser_signoff: true,
    blocks_exchange: true,
    create: {
        create_condition_description: "Instructed transfer or remortgage cases",
        predicate: (c) =>
            c.case.is_instructed !== null
            && (c.case.transaction_type === "transfer" || c.case.transaction_type === "remortgage"),
    },
    checklist: [
        ...preCompletionReadyItemsBeforeClientsAuthorised,
        clientsAuthorisedExchangeAndCompleteItem(preCompletionReadyItemsBeforeClientsAuthorised, {
            shortDescription: "Clients authorised to complete",
            longDescription: `Recording the clients' authorisation to complete on the case.`,
        }),
    ],
};
